import React, { useEffect } from "react";
import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Wind, Flame, Dumbbell, Gamepad2, Palette, Wrench, CalendarClock, Ruler, ChefHat, Package, Apple,
  ShieldCheck, LifeBuoy, FileText, ChevronRight, Mail, Smartphone,
  ClipboardCheck, Backpack, Activity, Scissors, Trophy, Tent,
  Hammer, Drill, Languages, Plane, Shirt, Syringe, NotebookPen, Puzzle
} from "lucide-react";
import { STUDIO, APPS, getApp } from "@/pages/apps/appsData";

const ICONS = { Wind, Flame, Dumbbell, Gamepad2, Palette, Wrench, CalendarClock, Ruler, ChefHat, Package, ClipboardCheck, Backpack, Activity, Scissors, Trophy, Tent, Hammer, Drill, Languages, Plane, Shirt, Syringe, NotebookPen, Puzzle };

/* ------------------------------------------------------------------ */
/* SEO — per-route title / description / canonical / og / json-ld      */
/* ------------------------------------------------------------------ */
const SITE = "https://viralcrew.de";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function useSeo(title, description, jsonld) {
  const { pathname } = useLocation();
  useEffect(() => {
    const url = SITE + pathname;
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:image", SITE + "/vc-logo.jpg");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", SITE + "/vc-logo.jpg");
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
    let s = document.getElementById("studio-jsonld");
    if (jsonld) {
      if (!s) {
        s = document.createElement("script");
        s.type = "application/ld+json";
        s.id = "studio-jsonld";
        document.head.appendChild(s);
      }
      s.textContent = JSON.stringify(jsonld);
    } else if (s) {
      s.remove();
    }
  }, [title, description, pathname, jsonld]);
}

function StudioLogo({ size = 28, rounded = 6 }) {
  return (
    <img src="/vc-logo.jpg" alt="ViralCrew Studio Logo" width={size} height={size}
      style={{ width: size, height: size, borderRadius: rounded, objectFit: "cover" }} loading="eager" />
  );
}

/* ------------------------------------------------------------------ */
/* Shell                                                               */
/* ------------------------------------------------------------------ */
function StudioFooter() {
  return (
    <footer className="border-t border-white/10 bg-[#050505]">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
        <div className="flex items-center gap-2">
          <StudioLogo size={22} rounded={6} />
          <span className="font-display font-black tracking-tight text-sm text-white">ViralCrew Studio</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link to="/apps" className="hover:text-white" data-testid="studio-footer-home">Studio</Link>
          <Link to="/apps/impressum" className="hover:text-white" data-testid="studio-footer-impressum">Impressum</Link>
          <Link to="/apps/datenschutz" className="hover:text-white" data-testid="studio-footer-datenschutz">Datenschutz</Link>
          <Link to="/apps/agb" className="hover:text-white" data-testid="studio-footer-agb">AGB</Link>
        </div>
        <p>© {new Date().getFullYear()} {STUDIO.owner}</p>
      </div>
    </footer>
  );
}

function StudioHeader() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/apps" className="flex items-center gap-2.5" data-testid="studio-nav-logo">
          <StudioLogo size={28} rounded={8} />
          <span className="font-display font-black tracking-tight text-lg">ViralCrew <span className="text-zinc-500">Studio</span></span>
        </Link>
        <Link to="/" className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition" data-testid="studio-to-agency">
          viralcrew.de
        </Link>
      </div>
    </header>
  );
}

function Shell({ title, subtitle, children, back = "/apps", backLabel = "Zurück zum Studio", wide }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <StudioHeader />
      <section className={`${wide ? "max-w-5xl" : "max-w-3xl"} w-full mx-auto px-6 md:px-10 py-14 flex-1`}>
        {back && (
          <Link to={back} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-white mb-8" data-testid="studio-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> {backLabel}
          </Link>
        )}
        <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl mb-4 break-words hyphens-auto">{title}</h1>
        {subtitle && <p className="text-zinc-400 text-lg mb-8">{subtitle}</p>}
        {children}
      </section>
      <StudioFooter />
    </div>
  );
}

const proseClass =
  "prose prose-invert max-w-none text-zinc-300 leading-relaxed space-y-5 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-display [&_h3]:font-bold [&_h3]:text-white [&_h3]:mt-6 [&_h3]:mb-2 [&_a]:text-[#FF0050] [&_a]:underline-offset-4 hover:[&_a]:underline";

/* ------------------------------------------------------------------ */
/* Studio home                                                         */
/* ------------------------------------------------------------------ */
export function StudioHome() {
  useSeo(
    "ViralCrew Studio · Indie iOS Apps – privacy-first, ohne Tracking",
    "ViralCrew Studio baut fokussierte iOS-Apps – privacy-first, ohne Accounts, ohne Tracking. Alle Daten bleiben lokal auf deinem Gerät.",
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "ViralCrew Studio – Indie iOS Apps",
      "url": SITE + "/apps",
      "description": "Übersicht der privacy-first iOS-Apps von ViralCrew Studio.",
    }
  );
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <StudioHeader />
      <section className="relative overflow-hidden flex-1">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-20 w-[500px] h-[500px] bg-[#FF0050] opacity-20 blur-[150px] rounded-full" />
          <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-[#00F2FE] opacity-10 blur-[150px] rounded-full" />
        </div>
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-16">
          <span className="inline-block text-xs font-mono uppercase tracking-wider text-[#00F2FE] mb-4">// Indie iOS App Studio</span>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl leading-[0.95] mb-5 max-w-3xl">
            Kleine Apps, die <span className="gradient-text">dein Leben</span> ein Stück besser machen.
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mb-4">
            ViralCrew Studio baut fokussierte iOS-Apps – privacy-first, ohne Accounts, ohne Tracking. Deine Daten bleiben auf deinem Gerät.
          </p>
          <div className="flex flex-wrap gap-3 mb-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-400 border border-white/10 rounded-full px-3 py-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#00E676]" />Kein Tracking</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-400 border border-white/10 rounded-full px-3 py-1.5"><Smartphone className="h-3.5 w-3.5 text-[#00F2FE]" />Daten lokal</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-400 border border-white/10 rounded-full px-3 py-1.5">4+ · Familienfreundlich</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {APPS.map((app) => {
              const Icon = ICONS[app.icon] || Wind;
              return (
                <Link
                  key={app.slug}
                  to={`/apps/${app.slug}`}
                  data-testid={`studio-app-tile-${app.slug}`}
                  className="group rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-white/25 transition"
                >
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4 border border-white/10 overflow-hidden group-hover:scale-110 transition-transform" style={app.iconImage ? {} : { backgroundColor: `${app.accent}1a`, color: app.accent }}>
                    {app.iconImage ? <img src={app.iconImage} alt={`${app.name} App-Icon`} className="h-full w-full object-cover" loading="lazy" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{app.category}</div>
                  <h3 className="font-display font-bold text-xl mb-1.5">{app.name}</h3>
                  <p className="text-zinc-400 text-sm mb-3">{app.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: app.accent }}>
                    Ansehen <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <StudioFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* App landing                                                         */
/* ------------------------------------------------------------------ */
function AppStoreBadge() {
  return (
    <div aria-disabled="true" data-testid="appstore-badge" title="Bald im App Store"
      className="inline-flex items-center gap-3 bg-white/90 text-black rounded-xl px-5 py-2.5 font-semibold cursor-default select-none">
      <Apple className="h-5 w-5" />
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-normal opacity-70">Bald im App Store</span>
        <span className="block text-sm">Coming soon on the App Store</span>
      </span>
    </div>
  );
}

export function AppLanding() {
  const { app: slug } = useParams();
  const app = getApp(slug);
  useSeo(
    app ? `${app.name} – ${app.tagline}` : "App · ViralCrew Studio",
    app ? `${app.name}: ${app.description}` : "ViralCrew Studio – privacy-first iOS Apps.",
    app ? {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": app.name,
      "applicationCategory": app.category,
      "operatingSystem": "iOS",
      "description": app.description,
      "url": SITE + "/apps/" + app.slug,
    } : null
  );
  if (!app) return <Navigate to="/apps" replace />;
  const Icon = ICONS[app.icon] || Wind;

  const subPages = [
    { to: `/apps/${app.slug}/privacy`, label: "Datenschutz", icon: ShieldCheck },
    { to: `/apps/${app.slug}/support`, label: "Support", icon: LifeBuoy },
    { to: `/apps/${app.slug}/terms`, label: "Nutzungsbedingungen", icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <StudioHeader />
      <section className="relative overflow-hidden flex-1">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 left-1/3 w-[500px] h-[500px] opacity-20 blur-[150px] rounded-full" style={{ backgroundColor: app.accent }} />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
          <Link to="/apps" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-white mb-8" data-testid="studio-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Alle Apps
          </Link>

          <div className="flex items-start gap-5 mb-8">
            <div className="h-20 w-20 shrink-0 rounded-3xl flex items-center justify-center overflow-hidden border border-white/10" style={app.iconImage ? {} : { backgroundColor: `${app.accent}1a`, color: app.accent }}>
              {app.iconImage ? <img src={app.iconImage} alt={app.name} className="h-full w-full object-cover" /> : <Icon className="h-9 w-9" />}
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{app.category} · 4+</div>
              <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl">{app.name}</h1>
              <p className="text-zinc-300 text-lg mt-2">{app.tagline}</p>
            </div>
          </div>

          <p className="text-zinc-400 text-base max-w-2xl mb-4">{app.description}</p>
          {app.disclaimer && (
            <p className="text-xs text-zinc-500 border-l-2 border-white/10 pl-3 max-w-2xl mb-6" data-testid="app-disclaimer">{app.disclaimer}</p>
          )}

          <div className="mb-10"><AppStoreBadge /></div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="font-display font-bold text-xl mb-4">Highlights</h2>
              <ul className="space-y-2.5">
                {app.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: app.accent }} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-display font-bold text-xl mb-4">{app.hero && !app.screenshots ? "Vorschau" : "Screenshots"}</h2>
              {app.screenshots ? (
                <div className="grid grid-cols-3 gap-3">
                  {app.screenshots.map((src, i) => (
                    <img key={i} src={src} alt={`${app.name} Screenshot ${i + 1}`} loading="lazy" data-testid={`app-screenshot-${i}`}
                      className="w-full rounded-xl border border-white/10 bg-[#0A0A0A]" />
                  ))}
                </div>
              ) : app.hero ? (
                <div className="flex justify-center">
                  <div className="relative w-full max-w-[240px] rounded-[2rem] border-[6px] border-white/10 bg-black p-1 shadow-2xl">
                    <img src={app.hero} alt={`${app.name} Screenshot`} loading="lazy" data-testid="app-screenshot-0"
                      className="w-full rounded-[1.5rem] block" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="aspect-[9/19] rounded-xl border border-white/10 bg-[#0A0A0A] flex items-center justify-center text-[10px] font-mono text-zinc-600" data-testid={`app-screenshot-${i}`}>
                      Screenshot
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {app.privacyIntro && (
            <div className="rounded-2xl border border-[#00E676]/25 bg-[#00E676]/5 p-5 mb-8" data-testid="app-privacy-short">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="h-4 w-4 text-[#00E676]" />
                <h2 className="font-display font-bold text-lg">Datenschutz in Kürze</h2>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">{app.privacyIntro}</p>
              <p className="text-zinc-500 text-xs mt-2">Data Not Collected · local-first · kein ViralCrew-Konto · kein Backend · kein Tracking · keine Werbung.</p>
            </div>
          )}

          {app.pricing?.freeIncludes && app.pricing?.proIncludes && (
            <div className="mb-8" data-testid="app-free-pro">
              <h2 className="font-display font-bold text-xl mb-4">Free vs. {app.pricing.proName || "Pro"}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-5">
                  <div className="font-display font-bold mb-3">Free</div>
                  <ul className="space-y-2">{app.pricing.freeIncludes.map((x, i) => (<li key={i} className="flex gap-2 text-sm text-zinc-300"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-zinc-500 shrink-0" />{x}</li>))}</ul>
                </div>
                <div className="rounded-xl border p-5" style={{ borderColor: `${app.accent}55`, backgroundColor: `${app.accent}0d` }}>
                  <div className="font-display font-bold mb-3">{app.pricing.proName || "Pro"}</div>
                  <ul className="space-y-2">{app.pricing.proIncludes.map((x, i) => (<li key={i} className="flex gap-2 text-sm text-zinc-200"><span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: app.accent }} />{x}</li>))}</ul>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Monatsabo, Jahresabo{app.pricing.trialDays ? ` (mit möglicher ${app.pricing.trialDays}-tägiger Testphase für berechtigte neue Abos)` : ""} und Lifetime als Einmalkauf.
                Preis, Währung und Trial-Berechtigung variieren je Land; verbindlich ist Apples Kaufdialog.
              </p>
            </div>
          )}

          {app.faq?.length > 0 && (
            <div className="mb-10" data-testid="app-faq-teaser">
              <h2 className="font-display font-bold text-xl mb-4">Häufige Fragen</h2>
              <div className="space-y-2">
                {app.faq.slice(0, 3).map((item, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3 text-sm text-zinc-300">{item.q}</div>
                ))}
              </div>
              <Link to={`/apps/${app.slug}/support`} className="inline-flex items-center gap-1 text-sm font-medium mt-3" style={{ color: app.accent }} data-testid="app-faq-support-link">
                Alle Fragen &amp; Support <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            {subPages.map((p) => (
              <Link key={p.to} to={p.to} data-testid={`app-link-${p.label.toLowerCase()}`}
                className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3 hover:border-white/25 transition text-sm">
                <p.icon className="h-4 w-4 text-zinc-500" />{p.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
      <StudioFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* App privacy (auto-generated from flags)                             */
/* ------------------------------------------------------------------ */
export function AppPrivacy() {
  const { app: slug } = useParams();
  const app = getApp(slug);
  useSeo(
    app ? `Datenschutz · ${app.name} – ViralCrew Studio` : "Datenschutz · ViralCrew Studio",
    app ? `Datenschutzerklärung der App ${app.name}: Data Not Collected, local-first, ohne Tracking. ${app.name} speichert alle Inhalte lokal auf deinem Gerät.` : "Datenschutz – ViralCrew Studio."
  );
  if (!app) return <Navigate to="/apps" replace />;
  const f = app.flags;
  const email = app.supportEmail || STUDIO.email;

  return (
    <Shell title={`Datenschutz · ${app.name}`} subtitle="App-spezifische Datenschutzerklärung" back={`/apps/${app.slug}`} backLabel={`Zurück zu ${app.name}`}>
      <div className={proseClass}>
        {app.privacyIntro && (
          <div className="not-prose rounded-xl border border-[#00E676]/30 bg-[#00E676]/5 p-4 text-[#c9ffe4] text-sm">
            {app.privacyIntro}
          </div>
        )}
        <p>
          Diese Erklärung beschreibt die Datenverarbeitung in der App <strong>{app.name}</strong>. Sie ergänzt unsere{" "}
          <Link to="/apps/datenschutz">zentrale Datenschutzerklärung</Link>. Verantwortlicher ist {STUDIO.owner},{" "}
          <a href={`mailto:${email}`}>{email}</a>.
        </p>

        <h2>Grundsatz</h2>
        <p>
          {app.name} speichert alle Inhalte <strong>ausschließlich lokal auf deinem Gerät</strong>. Es gibt kein Backend von uns,
          keine Benutzerkonten, kein Tracking, keine Analyse-SDKs und keine Werbung. Wir erheben und empfangen keine
          personenbezogenen Daten über die App.
        </p>

        {f.reminders && (
          <>
            <h2>Erinnerungen</h2>
            <p>
              Optionale Erinnerungen sind <strong>lokale Benachrichtigungen</strong>, die dein Gerät erzeugt. Es findet kein
              Versand über einen Server statt und wir senden keine Push-Nachrichten.
            </p>
          </>
        )}

        {f.healthkit && (
          <>
            <h2>Apple Health (HealthKit)</h2>
            {app.healthNote ? (
              <p>{app.healthNote}</p>
            ) : (
              <p>
                Die Apple-Health-Anbindung ist <strong>optional</strong> und nur aktiv, wenn du sie ausdrücklich freigibst. In dem
                Fall <strong>schreibt</strong> {app.name} abgeschlossene Workouts (z. B. Dauer und verbrannte Kalorien) in Apple
                Health. Wir <strong>lesen keine</strong> Gesundheitsdaten und übertragen nichts an uns oder Dritte – alle Daten
                bleiben auf deinem Gerät bzw. in Apple Health unter deiner Kontrolle.
              </p>
            )}
            <p>
              Die Freigabe kannst du jederzeit in den iOS-Einstellungen &gt; Datenschutz &amp; Sicherheit &gt; Health widerrufen.
            </p>
          </>
        )}

        {app.locationNote && (
          <>
            <h2>Standort &amp; Karten</h2>
            <p>{app.locationNote}</p>
          </>
        )}

        {app.cameraNote && (
          <>
            <h2>Kamera &amp; Fotos</h2>
            <p>{app.cameraNote}</p>
          </>
        )}

        {f.openai && (
          <>
            <h2>KI-Coach</h2>
            <p>
              Der KI-Coach arbeitet <strong>standardmäßig komplett auf dem Gerät</strong> (Apple On-Device-KI) – dabei verlassen
              keine Daten dein Gerät.
            </p>
            <p>
              Optional kannst du einen <strong>eigenen OpenAI-API-Schlüssel</strong> hinterlegen. Nur dann werden deine
              Coach-Anfragen zur Verarbeitung an OpenAI übermittelt. In diesem Fall ist die OpenAI, L.L.C. (USA) Empfänger bzw.
              Auftragsverarbeiter; es gelten deren Datenschutzbestimmungen (<a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">openai.com/policies/privacy-policy</a>).
              Ohne eigenen Schlüssel findet keine Datenübermittlung an Dritte statt. Diese Übermittlung in die USA beruht auf
              deiner Einwilligung (Art. 6 Abs. 1 lit. a, Art. 49 Abs. 1 lit. a DSGVO); den Schlüssel kannst du jederzeit wieder
              entfernen.
            </p>
          </>
        )}

        {f.gamecenter && (
          <>
            <h2>Game Center</h2>
            {app.gamecenterNote ? (
              <p>{app.gamecenterNote}</p>
            ) : (
              <p>
                {app.name} nutzt <strong>Apple Game Center</strong> für Bestenlisten. Diese Daten werden von Apple unter deren
                Datenschutzbestimmungen verwaltet. Wir sehen lediglich den von dir gewählten Game-Center-Alias – keine weiteren
                personenbezogenen Daten.
              </p>
            )}
          </>
        )}

        <h2>In-App-Käufe &amp; Abonnements</h2>
        <p>
          Käufe und Abos werden <strong>ausschließlich über Apple</strong> (App Store / StoreKit) abgewickelt. Wir erhalten von
          Apple nur anonymisierte Verkaufszahlen, <strong>keine Zahlungsdaten</strong> und keine personenbezogenen Daten.
        </p>

        {f.imports && app.importNote && (
          <>
            <h2>Importe</h2>
            <p>{app.importNote}</p>
          </>
        )}

        {f.exports && (
          <>
            <h2>Exporte &amp; Teilen</h2>
            {app.exportNote ? (
              <p>{app.exportNote}</p>
            ) : (
              <p>
                Exporte (z. B. PDF, CSV oder JSON) werden ausschließlich durch dich über das Teilen-Menü von Apple ausgelöst. Die
                Daten verlassen die App nur an das von dir gewählte Ziel (z. B. deine Mail- oder Dateien-App). Wir erhalten dabei
                nichts und speichern keine Kopie.
              </p>
            )}
          </>
        )}

        {app.disclaimer && (
          <>
            <h2>Wichtiger Hinweis</h2>
            <p>{app.disclaimer}</p>
          </>
        )}

        <h2>Deine Rechte</h2>
        <p>
          Da wir über die App keine personenbezogenen Daten erheben, liegen bei uns in der Regel keine Daten zu deiner Person vor.
          Für Anfragen zu Auskunft, Berichtigung oder Löschung erreichst du uns unter <a href={`mailto:${email}`}>{email}</a>.
          Details zu deinen Rechten findest du in der <Link to="/apps/datenschutz">zentralen Datenschutzerklärung</Link>.
        </p>
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
/* App support                                                         */
/* ------------------------------------------------------------------ */
export function AppSupport() {
  const { app: slug } = useParams();
  const app = getApp(slug);
  useSeo(
    app ? `Support · ${app.name} – ViralCrew Studio` : "Support · ViralCrew Studio",
    app ? `Support und häufige Fragen zu ${app.name}. Kontakt: ${app.supportEmail || STUDIO.email}. Kaufwiederherstellung und Aboverwaltung über Apple.` : "Support – ViralCrew Studio."
  );
  if (!app) return <Navigate to="/apps" replace />;
  const email = app.supportEmail || STUDIO.email;

  const faq = app.faq || [
    { q: "Werden meine Daten irgendwo gespeichert?", a: `Nein. ${app.name} speichert alles lokal auf deinem Gerät. Es gibt kein Konto und kein Backend von uns.` },
    ...(app.flags.reminders ? [{ q: "Warum bekomme ich keine Erinnerungen?", a: "Prüfe in den iOS-Einstellungen unter „Mitteilungen“, ob Benachrichtigungen für die App erlaubt sind." }] : []),
    ...(app.flags.healthkit ? [{ q: "Wie verbinde ich Apple Health?", a: "In den App-Einstellungen kannst du die Health-Anbindung aktivieren. Wir schreiben dann nur abgeschlossene Workouts – wir lesen keine Gesundheitsdaten." }] : []),
    ...(app.flags.openai ? [{ q: "Brauche ich einen OpenAI-Schlüssel für den KI-Coach?", a: "Nein. Der Coach läuft standardmäßig auf dem Gerät. Ein eigener OpenAI-Schlüssel ist optional und sendet deine Anfragen nur dann an OpenAI." }] : []),
    ...(app.flags.gamecenter ? [{ q: "Wie funktionieren die Bestenlisten?", a: "Über Apple Game Center. Melde dich in den iOS-Einstellungen bei Game Center an, um deine Platzierung zu sehen." }] : []),
    { q: "Wie kündige ich mein Abo?", a: "Abos verwaltest und kündigst du in den App-Store-Einstellungen deines Apple-Kontos (Einstellungen > [dein Name] > Abonnements)." },
    { q: "Ich habe einen Fehler gefunden – was tun?", a: `Schreib uns kurz an ${email} mit deinem Gerät und der iOS-Version. Wir kümmern uns.` },
  ];

  return (
    <Shell title={`Support · ${app.name}`} subtitle="Wir helfen dir gerne weiter." back={`/apps/${app.slug}`} backLabel={`Zurück zu ${app.name}`}>
      <a href={`mailto:${email}?subject=${encodeURIComponent(`Support: ${app.name}`)}`} data-testid="support-email"
        className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A0A0A] p-5 hover:border-white/25 transition mb-10">
        <div className="h-10 w-10 rounded-lg bg-[#FF0050]/10 border border-[#FF0050]/30 flex items-center justify-center text-[#FF0050]">
          <Mail className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display font-bold">E-Mail-Support</div>
          <div className="text-zinc-400 text-sm font-mono">{email}</div>
        </div>
      </a>

      <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-4 text-sm text-zinc-400 mb-10" data-testid="support-privacy-note">
        Bitte nenne App-Version, iOS-Version und eine kurze Problembeschreibung. Sende keine persönlichen, sensiblen oder
        app-internen Daten unaufgefordert.{app.supportNoteExtra ? ` ${app.supportNoteExtra}` : ""}
      </div>

      <h2 className="font-display font-bold text-2xl mb-4">Häufige Fragen</h2>
      <div className="space-y-3">
        {faq.map((item, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-[#0A0A0A] p-5" data-testid={`support-faq-${i}`}>
            <div className="font-display font-bold text-white mb-1.5">{item.q}</div>
            <p className="text-zinc-400 text-sm leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
/* App terms                                                           */
/* ------------------------------------------------------------------ */
export function AppTerms() {
  const { app: slug } = useParams();
  const app = getApp(slug);
  useSeo(
    app ? `Nutzungsbedingungen · ${app.name} – ViralCrew Studio` : "Nutzungsbedingungen · ViralCrew Studio",
    app ? `Nutzungsbedingungen (EULA) für ${app.name}: Free/Pro, Apple-Käufe, automatische Aboverlängerung, Kündigung im Apple-Account, Lifetime als Einmalkauf.` : "Nutzungsbedingungen – ViralCrew Studio."
  );
  if (!app) return <Navigate to="/apps" replace />;
  const p = app.pricing;

  return (
    <Shell title={`Nutzungsbedingungen · ${app.name}`} subtitle="EULA für die App" back={`/apps/${app.slug}`} backLabel={`Zurück zu ${app.name}`}>
      <div className={proseClass}>
        <p>
          Diese Bedingungen gelten für die Nutzung der App <strong>{app.name}</strong> und ergänzen unsere{" "}
          <Link to="/apps/agb">allgemeinen Nutzungsbedingungen (EULA)</Link>. Anbieter ist {STUDIO.owner}.
        </p>

        <h2>1. Lizenz</h2>
        <p>
          Du erhältst ein einfaches, nicht übertragbares Recht, {app.name} auf den mit deinem Apple-Konto verbundenen Geräten für
          private Zwecke zu nutzen. Zusätzlich gilt die Standard-Lizenzvereinbarung von Apple für Apps (Apple „Licensed
          Application End User License Agreement“).
        </p>

        <h2>2. Abonnements &amp; Preise</h2>
        {p ? (
          <>
            <p>{p.proName || `${app.name} Pro`} ist ein optionales Upgrade und wird ausschließlich über Apple (App Store / StoreKit) angeboten:</p>
            {p.noAmounts ? (
              <ul>
                <li><strong>Monatsabo</strong> – monatliche Abrechnung.</li>
                <li><strong>Jahresabo</strong> – jährliche Abrechnung{p.trialDays ? <>, inkl. <strong>{p.trialDays} Tage kostenloser Testphase</strong> für berechtigte neue Jahresabos (die Berechtigung bestimmt Apple)</> : null}.</li>
                <li><strong>Lifetime</strong> – einmaliger Kauf, kein Abo.</li>
              </ul>
            ) : (
              <ul>
                <li><strong>Monatsabo</strong> – {p.monthly}.</li>
                <li><strong>Jahresabo</strong> – {p.yearly}, inkl. <strong>{p.trialDays} Tage kostenloser Testphase</strong> (nur Jahresabo).</li>
                <li><strong>Lifetime</strong> – {p.lifetime} (einmaliger Kauf, kein Abo).</li>
              </ul>
            )}
            <p>
              Preis, Währung und Trial-Berechtigung können je nach Land/Storefront abweichen; <strong>verbindlich ist stets Apples
              Kaufdialog</strong> im App Store. Eine nicht rechtzeitig beendete Testphase geht automatisch in das bezahlte Jahresabo über.
            </p>
            {p.freeIncludes && p.proIncludes && (
              <div className="not-prose grid sm:grid-cols-2 gap-4 my-6">
                <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-5">
                  <div className="font-display font-bold mb-3">Free</div>
                  <ul className="space-y-2">{p.freeIncludes.map((x, i) => (<li key={i} className="flex gap-2 text-sm text-zinc-300"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-zinc-500 shrink-0" />{x}</li>))}</ul>
                </div>
                <div className="rounded-xl border p-5" style={{ borderColor: `${app.accent}55`, backgroundColor: `${app.accent}0d` }}>
                  <div className="font-display font-bold mb-3">{p.proName || "Pro"}</div>
                  <ul className="space-y-2">{p.proIncludes.map((x, i) => (<li key={i} className="flex gap-2 text-sm text-zinc-200"><span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: app.accent }} />{x}</li>))}</ul>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <p>Sofern {app.name} kostenpflichtige Funktionen anbietet, gelten folgende Beispiel-Bedingungen (die konkreten Preise werden im App Store angezeigt):</p>
            <ul>
              <li><strong>Monatsabo</strong> – monatliche Abrechnung.</li>
              <li><strong>Jahresabo</strong> – jährliche Abrechnung, inkl. <strong>7 Tage kostenloser Testphase</strong>.</li>
              <li><strong>Lifetime</strong> – einmaliger Kauf, kein Abo.</li>
            </ul>
          </>
        )}
        <p>
          Abos <strong>verlängern sich automatisch</strong>, sofern sie nicht mindestens 24 Stunden vor Ablauf des laufenden
          Zeitraums gekündigt werden. Die Abrechnung erfolgt über dein Apple-Konto. Kündigen und verwalten kannst du jederzeit in
          den <strong>App-Store-Einstellungen</strong> (Einstellungen &gt; [dein Name] &gt; Abonnements). Eine bereits begonnene
          Testphase verfällt anteilig, wenn du ein Abo abschließt.
        </p>

        {app.disclaimer && (
          <>
            <h2>Wichtiger Hinweis</h2>
            <p>{app.disclaimer}</p>
          </>
        )}

        <h2>3. Widerruf &amp; Erstattungen</h2>
        <p>
          Käufe und Erstattungen werden über Apple abgewickelt. Erstattungsanfragen richtest du bitte direkt an Apple
          (reportaproblem.apple.com).
        </p>

        <h2>4. Gewährleistung</h2>
        <p>
          Die App wird „wie besehen“ bereitgestellt. Soweit gesetzlich zulässig, übernehmen wir keine Gewähr für ununterbrochene
          oder fehlerfreie Verfügbarkeit.
        </p>

        <h2>5. Haftung</h2>
        <p>
          Unsere Haftung ist – soweit gesetzlich zulässig – ausgeschlossen. Zwingende gesetzliche Haftung (insb. bei Vorsatz,
          grober Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit) bleibt unberührt.
        </p>

        <h2>6. Apple</h2>
        <p>
          Apple ist nicht Vertragspartner dieser Bedingungen, aber berechtigter Dritter und kann diese Bedingungen ihr gegenüber
          durchsetzen.
        </p>

        <h2>7. Anwendbares Recht</h2>
        <p>Es gilt deutsches Recht. Gerichtsstand ist – soweit zulässig – Solingen.</p>
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
/* Global studio legal pages                                           */
/* ------------------------------------------------------------------ */
export function StudioImpressum() {
  useSeo("Impressum · ViralCrew Studio", "Impressum und Anbieterkennzeichnung von ViralCrew Studio (Indie iOS Apps).");
  return (
    <Shell title="Impressum" subtitle="Angaben gemäß § 5 DDG (ehem. TMG)">
      <div className={proseClass}>
        <h2>Anbieter</h2>
        <p>
          {STUDIO.owner}<br />
          {STUDIO.address.map((l, i) => (<span key={i}>{l}<br /></span>))}
        </p>
        <h2>Kontakt</h2>
        <p>
          Telefon: <a href={`tel:${STUDIO.phone.replace(/\s/g, "")}`}>{STUDIO.phone}</a><br />
          E-Mail: <a href={`mailto:${STUDIO.email}`}>{STUDIO.email}</a>
        </p>
        <h2>Umsatzsteuer-ID</h2>
        <p>Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz: <strong>{STUDIO.vat}</strong></p>
        <h2>Verantwortlich i.S.d. § 18 Abs. 2 MStV</h2>
        <p>{STUDIO.owner}, Anschrift wie oben.</p>
        <h2>EU-Streitschlichtung</h2>
        <p>
          Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
          <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a>.
          Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
          teilzunehmen.
        </p>
        <h2>Apps</h2>
        <p>
          Über dieses Studio veröffentlichen wir die iOS-Apps: {APPS.map((a) => a.name).join(", ")}. „Apple“, „App Store“, „Apple
          Health“ und „Game Center“ sind Marken der Apple Inc. Wir stehen in keinem gesellschaftsrechtlichen Verhältnis zu Apple.
        </p>
      </div>
    </Shell>
  );
}

export function StudioDatenschutz() {
  useSeo("Datenschutzerklärung · ViralCrew Studio", "Datenschutzerklärung für viralcrew.de/apps und die iOS-Apps von ViralCrew Studio – kein Tracking, keine Werbung, Daten bleiben lokal.");
  return (
    <Shell title="Datenschutzerklärung" subtitle="Für viralcrew.de/apps und unsere iOS-Apps" wide>
      <div className={proseClass}>
        <h2>1. Verantwortlicher</h2>
        <p>
          {STUDIO.owner}, {STUDIO.address.join(", ")} · <a href={`mailto:${STUDIO.email}`}>{STUDIO.email}</a> · {STUDIO.phone}
        </p>

        <h2>2. Überblick</h2>
        <p>
          Datenschutz ist bei uns Kern des Produkts. Unsere Apps erheben <strong>keine</strong> personenbezogenen Daten, haben
          <strong> kein Backend</strong>, keine Accounts, kein Tracking und keine Werbung. Alle App-Daten bleiben lokal auf deinem
          Gerät. Diese Website dient primär der Information und den Rechts-/Support-Seiten.
        </p>

        <h2>3. Hosting &amp; Server-Logs</h2>
        <p>
          Beim Aufruf der Website werden durch den Hosting-Anbieter technisch notwendige Zugriffsdaten (z. B. IP-Adresse,
          Datum/Uhrzeit, abgerufene Seite, Browsertyp) in Server-Logs verarbeitet. Rechtsgrundlage ist unser berechtigtes Interesse
          an einem sicheren, stabilen Betrieb (Art. 6 Abs. 1 lit. f DSGVO).
        </p>

        <h2>4. Cookies &amp; Tracking</h2>
        <p>
          Die App-Studio-Seiten (unter /apps) laden <strong>keine Analyse-, Tracking- oder Werbe-Dienste</strong> und setzen keine
          Marketing-Cookies. Es werden lediglich die unter Punkt 3 genannten, technisch notwendigen Zugriffsdaten durch den
          Hosting-Anbieter verarbeitet. (Andere Bereiche der Website können nach ausdrücklicher Einwilligung über einen
          Cookie-Hinweis Reichweitenmessung nutzen; auf den Studio-Seiten geschieht dies nicht.)
        </p>

        <h2>5. Kontaktaufnahme</h2>
        <p>
          Wenn du uns per E-Mail kontaktierst, verarbeiten wir deine Angaben ausschließlich zur Bearbeitung deiner Anfrage
          (Art. 6 Abs. 1 lit. b und lit. f DSGVO).
        </p>

        <h2>6. Datenschutz in unseren Apps</h2>
        <p>
          Alle unsere Apps speichern Inhalte nur lokal auf dem Gerät, ohne Konto und ohne Tracking. App-spezifische Besonderheiten:
        </p>
        <h3>Apple Health / HealthKit (FitMaxx)</h3>
        <p>
          Optional und nur nach deiner Freigabe schreibt FitMaxx abgeschlossene Workouts (Dauer, Kalorien) in Apple Health. Wir
          lesen keine Gesundheitsdaten und übertragen nichts an uns oder Dritte.
        </p>
        <h3>Optionaler KI-Coach mit OpenAI (FitMaxx)</h3>
        <p>
          Der KI-Coach läuft standardmäßig auf dem Gerät. Nur wenn du einen eigenen OpenAI-API-Schlüssel hinterlegst, werden deine
          Anfragen an OpenAI, L.L.C. (USA) übermittelt (Auftragsverarbeiter). Es gilt deren Datenschutzerklärung
          (<a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">openai.com/policies/privacy-policy</a>).
          Diese Übermittlung in ein Drittland (USA) beruht auf deiner Einwilligung (Art. 49 Abs. 1 lit. a DSGVO).
        </p>
        <h3>Game Center (NeonRush, ColorPour, BoltSort)</h3>
        <p>
          Für Bestenlisten wird Apple Game Center genutzt; die Daten verwaltet Apple unter deren Datenschutz. Wir sehen nur den von
          dir gewählten Game-Center-Alias.
        </p>
        <h3>In-App-Käufe &amp; Abos (alle Apps)</h3>
        <p>
          Zahlungen laufen ausschließlich über Apple (App Store / StoreKit). Wir erhalten nur anonymisierte Verkaufszahlen, keine
          Zahlungsdaten.
        </p>
        <h3>Erinnerungen</h3>
        <p>Erinnerungen sind lokale Benachrichtigungen auf dem Gerät – kein Server, kein Push von uns.</p>

        <h2>7. Empfänger &amp; Drittlandübermittlung</h2>
        <p>
          Wir geben keine personenbezogenen Daten weiter. Eine Übermittlung in ein Drittland (USA) findet nur im oben genannten
          OpenAI-Opt-in-Fall statt – auf Grundlage deiner Einwilligung und der von OpenAI zugesicherten Garantien.
        </p>

        <h2>8. Deine Rechte</h2>
        <p>
          Du hast das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung (Art. 18),
          Datenübertragbarkeit (Art. 20) und Widerspruch (Art. 21). Anfragen bitte an <a href={`mailto:${STUDIO.email}`}>{STUDIO.email}</a>.
        </p>

        <h2>9. Beschwerderecht</h2>
        <p>Du kannst dich bei der zuständigen Aufsichtsbehörde (Landesbeauftragte für Datenschutz NRW) beschweren.</p>

        <h2>10. Speicherdauer</h2>
        <p>Wir speichern Daten nur so lange, wie es für die jeweiligen Zwecke erforderlich ist oder gesetzliche Pflichten bestehen.</p>
      </div>
    </Shell>
  );
}

export function StudioAGB() {
  useSeo("Nutzungsbedingungen (EULA) · ViralCrew Studio", "Allgemeine Nutzungsbedingungen (EULA) für die iOS-Apps von ViralCrew Studio.");
  return (
    <Shell title="Nutzungsbedingungen (EULA)" subtitle="Für die Apps von ViralCrew Studio" wide>
      <div className={proseClass}>
        <h2>§ 1 Geltungsbereich</h2>
        <p>
          Diese Bedingungen gelten für die Nutzung der von {STUDIO.owner} (ViralCrew Studio, {STUDIO.address.join(", ")})
          veröffentlichten iOS-Apps sowie der Website viralcrew.de/apps.
        </p>

        <h2>§ 2 Nutzungslizenz</h2>
        <p>
          Du erhältst ein einfaches, nicht übertragbares, widerrufliches Recht zur privaten Nutzung der Apps auf den mit deinem
          Apple-Konto verbundenen Geräten. Ergänzend gilt die Standard-Lizenzvereinbarung von Apple für Apps (Apple „Licensed
          Application End User License Agreement“).
        </p>

        <h2>§ 3 Abonnements &amp; Preise</h2>
        <ul>
          <li><strong>Monatsabo</strong> – monatliche Abrechnung.</li>
          <li><strong>Jahresabo</strong> – jährliche Abrechnung, inkl. <strong>7 Tage kostenloser Testphase</strong>.</li>
          <li><strong>Lifetime</strong> – einmaliger Kauf ohne Abo.</li>
        </ul>
        <p>
          Die tatsächlichen Preise werden im App Store angezeigt. Abos <strong>verlängern sich automatisch</strong>, sofern nicht
          mindestens 24 Stunden vor Ablauf gekündigt wird. Abrechnung, Verwaltung und <strong>Kündigung erfolgen über die
          App-Store-Einstellungen</strong> deines Apple-Kontos. Käufe und Erstattungen werden von Apple abgewickelt.
        </p>

        <h2>§ 4 Pflichten der Nutzer</h2>
        <p>Die Apps dürfen nicht missbräuchlich, rechtswidrig oder in einer Weise genutzt werden, die Rechte Dritter verletzt.</p>

        <h2>§ 5 Gewährleistung</h2>
        <p>Die Apps werden „wie besehen“ bereitgestellt. Soweit gesetzlich zulässig, wird keine Gewähr für ununterbrochene oder fehlerfreie Nutzbarkeit übernommen.</p>

        <h2>§ 6 Haftung</h2>
        <p>
          Eine Haftung von {STUDIO.owner} ist – soweit gesetzlich zulässig – ausgeschlossen. Zwingende gesetzliche Haftung (insb.
          bei Vorsatz, grober Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit, sowie nach dem
          Produkthaftungsgesetz) bleibt unberührt.
        </p>

        <h2>§ 7 Änderungen</h2>
        <p>Wir können diese Bedingungen anpassen, soweit dies zumutbar ist; die jeweils aktuelle Fassung ist hier abrufbar.</p>

        <h2>§ 8 Apple als berechtigter Dritter</h2>
        <p>Apple ist nicht Vertragspartei, aber berechtigter Dritter und kann diese Bedingungen ihr gegenüber durchsetzen.</p>

        <h2>§ 9 Schlussbestimmungen</h2>
        <p>Es gilt deutsches Recht. Gerichtsstand ist – soweit zulässig – Solingen. Sollten einzelne Bestimmungen unwirksam sein, bleibt der Vertrag im Übrigen wirksam.</p>

        <p className="text-sm text-zinc-500 mt-8">Stand: Juni 2026</p>
      </div>
    </Shell>
  );
}
